/**
 * 物品系统类型定义
 * 目标：为“统一定义 -> 实例化”链路提供 Schema
 */

import { z } from 'zod';
import { GameplayEffectSchema } from '@/IsekaiRPG/modules/effect';
import {
  GenerationMetaSchema,
  LibraryMetaSchema,
  LibraryMetaSourceSchema,
  LibraryMetaStatusSchema,
  LibraryMetaUniquenessSchema,
  type GenerationMeta,
  type LibraryMeta,
  type LibraryMetaSource,
} from '@/IsekaiRPG/shared/types';

// ============================================
// 基础枚举
// ============================================

export const ItemCategorySchema = z.enum(['equipment', 'consumable', 'material', 'quest', 'special']);
export type ItemCategory = z.infer<typeof ItemCategorySchema>;

export const EquipmentSlotSchema = z.enum(['weapon', 'armor', 'helmet', 'accessory1', 'accessory2']);
export type EquipmentSlot = z.infer<typeof EquipmentSlotSchema>;

export const ItemRaritySchema = z.enum(['common', 'uncommon', 'rare', 'epic', 'legendary']);
export type ItemRarity = z.infer<typeof ItemRaritySchema>;

export const ItemAffixCategorySchema = z.enum(['prefix', 'suffix', 'implicit', 'special']);
export type ItemAffixCategory = z.infer<typeof ItemAffixCategorySchema>;

export const ItemTemplateSourceSchema = z.enum(['builtin', 'quest', 'site', 'llm']);
export type ItemTemplateSource = z.infer<typeof ItemTemplateSourceSchema>;

const ItemTemplateLibraryMetaInputSchema = z.object({
  reusable: z.boolean().optional(),
  uniqueness: LibraryMetaUniquenessSchema.optional(),
  source: LibraryMetaSourceSchema.optional(),
  status: LibraryMetaStatusSchema.optional(),
  exposeToAi: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
});

function normalizeItemTemplateTags(tags: string[] | undefined): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  (tags || []).forEach(tag => {
    const nextTag = tag.trim();
    if (!nextTag) return;
    const lowerKey = nextTag.toLowerCase();
    if (seen.has(lowerKey)) return;
    seen.add(lowerKey);
    normalized.push(nextTag);
  });

  return normalized;
}

function mapItemTemplateSourceToLibraryMetaSource(source: ItemTemplateSource | undefined): LibraryMetaSource {
  if (source === 'builtin') return 'builtin';
  return 'ai_generated';
}

function resolveItemTemplateLibraryMeta(input: {
  source?: ItemTemplateSource;
  unique?: boolean;
  libraryMeta?: Partial<LibraryMeta>;
}): LibraryMeta {
  const explicitLibraryTags = normalizeItemTemplateTags(input.libraryMeta?.tags);
  const isUnique = input.unique === true;
  const isBuiltin = input.source === 'builtin';

  return LibraryMetaSchema.parse({
    reusable: !isUnique,
    uniqueness: isUnique ? 'unique' : 'generic',
    source: mapItemTemplateSourceToLibraryMetaSource(input.source),
    status: isUnique ? 'draft' : isBuiltin ? 'promoted' : 'candidate',
    exposeToAi: !isUnique,
    ...input.libraryMeta,
    ...(isUnique
      ? {
          reusable: false,
          uniqueness: 'unique' as const,
          status: 'draft' as const,
          exposeToAi: false,
        }
      : {}),
    tags: explicitLibraryTags,
  });
}

// ============================================
// 生成上下文与元数据
// ============================================

/**
 * 物品生成元数据（用于复现与追踪）
 */
export const ItemGenerationMetaSchema = GenerationMetaSchema;
export type ItemGenerationMeta = GenerationMeta;

/**
 * 掉落上下文（决定模板选择与预算）
 */
export const DropContextSchema = z.object({
  sourceType: z.enum(['quest_reward', 'enemy_drop', 'shop', 'craft', 'event']),
  sourceId: z.string().min(1),
  playerLevel: z.number().int().min(1),
  areaLevel: z.number().int().min(1).default(1),
  difficulty: z.number().int().min(1).max(10).default(1),
  luck: z.number().min(0).max(1).default(0),
});
export type DropContext = z.infer<typeof DropContextSchema>;

// ============================================
// 词条与需求
// ============================================

/**
 * 装备对技能的加成
 */
export const ItemSkillBonusSchema = z.object({
  skillId: z.string().min(1),
  value: z.number(),
});
export type ItemSkillBonus = z.infer<typeof ItemSkillBonusSchema>;

/**
 * 装备佩戴需求
 */
export const ItemRequirementSchema = z.object({
  skillId: z.string().min(1),
  minLevel: z.number().int().min(0),
});
export type ItemRequirement = z.infer<typeof ItemRequirementSchema>;

/**
 * 单个词条定义
 */
export const ItemAffixSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  category: ItemAffixCategorySchema.default('prefix'),
  tags: z.array(z.string()).default([]),
  // 用于控制过强组合
  conflictGroups: z.array(z.string()).default([]),
  weight: z.number().min(0).default(1),
  budgetCost: z.number().min(0).default(1),
  statModifiers: z.record(z.string(), z.number()).default({}),
});
export type ItemAffix = z.infer<typeof ItemAffixSchema>;

/**
 * 词条池（按物品分类维护）
 */
export const ItemAffixPoolSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  categories: z.array(ItemCategorySchema).min(1),
  affixes: z.array(ItemAffixSchema).default([]),
});
export type ItemAffixPool = z.infer<typeof ItemAffixPoolSchema>;

// ============================================
// 物品使用定义
// ============================================

export const ItemUseTargetSchema = z.enum(['selected_party_member']);
export type ItemUseTarget = z.infer<typeof ItemUseTargetSchema>;

export const ItemConsumePolicySchema = z.enum(['on_success', 'always']);
export type ItemConsumePolicy = z.infer<typeof ItemConsumePolicySchema>;

export const ItemUseDefinitionSchema = z.object({
  target: ItemUseTargetSchema.default('selected_party_member'),
  consumePolicy: ItemConsumePolicySchema.default('on_success'),
  effects: z.array(GameplayEffectSchema).min(1),
});
export type ItemUseDefinition = z.infer<typeof ItemUseDefinitionSchema>;

// ============================================
// 模板与实例
// ============================================

/**
 * 物品基础模板（统一定义层）
 */
const ItemTemplateCommonFields = {
  id: z.string().min(1),
  name: z.string().min(1),
  source: ItemTemplateSourceSchema.default('builtin'),
  sourceContext: z.record(z.string(), z.unknown()).optional(),
  unique: z.boolean().default(false),
  rarity: ItemRaritySchema.default('common'),
  description: z.string().default(''),
  icon: z.string().default(''),
  libraryMeta: ItemTemplateLibraryMetaInputSchema.optional(),
  levelRequirement: z.number().int().min(0).default(0),
  price: z.number().int().min(0).default(0),
  baseStats: z.record(z.string(), z.number()).default({}),
  requirements: z.array(ItemRequirementSchema).default([]),
  bonuses: z.array(ItemSkillBonusSchema).default([]),
};

const EquipmentItemTemplateSchema = z.object({
  ...ItemTemplateCommonFields,
  category: z.literal('equipment'),
  stackable: z.literal(false),
  equippable: z.literal(true),
  equipSlot: EquipmentSlotSchema,
  affixBudget: z.number().min(0).default(0),
  allowedAffixCategories: z.array(ItemAffixCategorySchema).default(['prefix', 'suffix']),
});

const ConsumableItemTemplateSchema = z.object({
  ...ItemTemplateCommonFields,
  category: z.literal('consumable'),
  stackable: z.boolean(),
  equippable: z.literal(false),
  equipSlot: z.undefined().optional(),
  useDefinition: ItemUseDefinitionSchema.optional(),
  affixBudget: z.number().min(0).default(0),
  allowedAffixCategories: z.array(ItemAffixCategorySchema).default([]),
});

const MaterialItemTemplateSchema = z.object({
  ...ItemTemplateCommonFields,
  category: z.literal('material'),
  stackable: z.boolean(),
  equippable: z.literal(false),
  equipSlot: z.undefined().optional(),
  affixBudget: z.number().min(0).default(0),
  allowedAffixCategories: z.array(ItemAffixCategorySchema).default([]),
});

const QuestItemTemplateSchema = z.object({
  ...ItemTemplateCommonFields,
  category: z.literal('quest'),
  stackable: z.boolean(),
  equippable: z.literal(false),
  equipSlot: z.undefined().optional(),
  affixBudget: z.number().min(0).default(0),
  allowedAffixCategories: z.array(ItemAffixCategorySchema).default([]),
});

const SpecialItemTemplateSchema = z.object({
  ...ItemTemplateCommonFields,
  category: z.literal('special'),
  stackable: z.boolean(),
  equippable: z.literal(false),
  equipSlot: z.undefined().optional(),
  affixBudget: z.number().min(0).default(0),
  allowedAffixCategories: z.array(ItemAffixCategorySchema).default([]),
});

const ItemBaseTemplateObjectSchema = z.discriminatedUnion('category', [
  EquipmentItemTemplateSchema,
  ConsumableItemTemplateSchema,
  MaterialItemTemplateSchema,
  QuestItemTemplateSchema,
  SpecialItemTemplateSchema,
]);

export const ItemBaseTemplateSchema = ItemBaseTemplateObjectSchema.transform(value => {
  return {
    ...value,
    libraryMeta: resolveItemTemplateLibraryMeta({
      source: value.source,
      unique: value.unique,
      libraryMeta: value.libraryMeta,
    }),
  };
});
export type ItemBaseTemplateInput = z.input<typeof ItemBaseTemplateSchema>;
export type ItemBaseTemplate = z.infer<typeof ItemBaseTemplateSchema>;

export function getItemTemplateLibraryTags(template: Pick<ItemBaseTemplate, 'libraryMeta'>): string[] {
  return normalizeItemTemplateTags(template.libraryMeta.tags || []);
}

export function shouldExposeItemTemplateToAi(template: Pick<ItemBaseTemplate, 'libraryMeta'>): boolean {
  return (
    template.libraryMeta.reusable !== false &&
    template.libraryMeta.exposeToAi !== false &&
    (template.libraryMeta.status === 'candidate' || template.libraryMeta.status === 'promoted')
  );
}

/**
 * 物品实例（不可堆叠物或生成装备）
 */
export const ItemInstanceSchema = z
  .object({
    id: z.string().min(1),
    definitionId: z.string().min(1),
    name: z.string().min(1),
    category: ItemCategorySchema,
    stackable: z.boolean().default(false),
    equippable: z.boolean().default(false),
    unique: z.boolean().default(false),
    equipSlot: EquipmentSlotSchema.optional(),
    rarity: ItemRaritySchema,
    description: z.string().default(''),
    icon: z.string().default(''),
    levelRequirement: z.number().int().min(0).default(0),
    price: z.number().int().min(0).default(0),
    finalStats: z.record(z.string(), z.number()).default({}),
    affixes: z.array(ItemAffixSchema).default([]),
    requirements: z.array(ItemRequirementSchema).default([]),
    bonuses: z.array(ItemSkillBonusSchema).default([]),
    metadata: ItemGenerationMetaSchema.default(() => ({
      schemaVersion: '2.0.0',
      createdAt: Date.now(),
    })),
  })
  .superRefine((value, ctx) => {
    if (value.equippable && !value.equipSlot) {
      ctx.addIssue({
        code: 'custom',
        message: '可装备实例必须提供 equipSlot',
      });
    }
    if (!value.equippable && value.equipSlot) {
      ctx.addIssue({
        code: 'custom',
        message: '不可装备实例不能提供 equipSlot',
      });
    }
    if (value.stackable && value.equippable) {
      ctx.addIssue({
        code: 'custom',
        message: '可堆叠实例不能同时可装备',
      });
    }
  });
export type ItemInstance = z.infer<typeof ItemInstanceSchema>;

// ============================================
// 类型守卫函数
// ============================================

/**
 * 检查是否为有效的物品分类
 */
export function isValidItemCategory(value: unknown): value is ItemCategory {
  return ItemCategorySchema.safeParse(value).success;
}

/**
 * 检查是否为有效的装备槽位
 */
export function isValidEquipmentSlot(value: unknown): value is EquipmentSlot {
  return EquipmentSlotSchema.safeParse(value).success;
}
